import type { DisclosureContentProps } from '@ariakit/react';
import { Dialog as AriakitDialog } from '@ariakit/react';
import { useRef } from 'react';

import type { ModalCloseReason } from '~/components/StandardModal/types';

type Props = {
  backdropProps?: Omit<DisclosureContentProps<'div'>, 'state'>;
  children?: React.ReactNode;
  className?: string;
  isOpen: boolean;
  onDismiss: (reason: ModalCloseReason) => void;
};

export const Dialog = ({ className, isOpen, onDismiss, children }: Props) => {
  // Ariakit's close event says nothing about what closed the dialog, so Escape and an outside
  // interaction note it before Ariakit hides; any other close is its DialogDismiss, the X.
  const closeReason = useRef<ModalCloseReason>('close_button');

  function closingBy(reason: ModalCloseReason) {
    closeReason.current = reason;

    return true;
  }

  function onClose() {
    onDismiss(closeReason.current);
    closeReason.current = 'close_button';
  }

  return (
    <AriakitDialog
      className={`fixed top-0 z-50 flex size-full items-center justify-center`}
      hideOnEscape={() => closingBy('escape_key')}
      hideOnInteractOutside={() => closingBy('backdrop')}
      onClose={onClose}
      open={isOpen}
      onClick={() => onDismiss('backdrop')}
      backdrop={<div className="bg-black/50" />}
    >
      <div
        className={className}
        role="button"
        tabIndex={0}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.stopPropagation();
          }
        }}
      >
        {children}
      </div>
    </AriakitDialog>
  );
};

export { DialogDismiss } from '@ariakit/react/dialog';
